"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { PanelLeft, PanelRight, ChevronRight } from "lucide-react";
import Search from "@/components/Search";
import { cn } from "@/lib/utils";

/**
 * 슬림 콘텐츠 헤더 — 상단 NAV 제거 후 본문 상단 (JCG-2060).
 * 좌: 좌측 사이드바 접기/펼치기 토글. 중앙: breadcrumb + 스크롤 시 h1 핀 고정(scroll-spy).
 * 우: 검색창. 핀 타이틀은 본문 `#article-h1` 텍스트를 읽어 표시한다.
 */
interface ContentHeaderProps {
  leftCollapsed?: boolean;
  onToggleSidebar?: () => void;
  rightCollapsed?: boolean;
  onToggleRightSidebar?: () => void;
  rightSidebarLabel?: string;
  categoryLabel: string;
  /** 스코프 라벨(JCG-2102, 이론/코딩 글). 인덱스 셸 custom 변형은 스코프가 없어 미지정. */
  scopeLabel?: string;
  /**
   * 상단 바 중앙 슬롯 ref setter (JCG-2102). 복원 게시판이 회차 셀렉터를 여기 portal.
   * 비면(이론/코딩 등) empty:hidden 으로 사라지고 기존 pinned breadcrumb 동작이 유지된다.
   */
  headerSlotRef?: (el: HTMLElement | null) => void;
  /**
   * 상단 바 우측 액션 슬롯 ref setter (JCG-2211). 글 페이지 LanguageToggle 이 여기 portal 되며,
   * 실제 액션이 있을 때 좁은 폭에서는 검색 버튼을 아이콘형으로 줄여 제목/액션 겹침을 막는다.
   */
  headerActionSlotRef?: (el: HTMLElement | null) => void;
  /**
   * 인덱스 셸(JCG-2104) 상시 제목. 지정 시 본문 #article-h1 scroll-spy 대신 이 제목을
   * 핀 없이 항상 표시한다(인덱스 페이지는 핀 대상 h1 이 없음).
   */
  staticTitle?: string;
  /** 좌측 사이드바 접기/펼치기 토글 노출 여부 (기본 true). 좌측 콘텐츠 컬럼이 없는
   *  셸(TestCenterShell)에서는 false 로 숨긴다. */
  showSidebarToggle?: boolean;
  /**
   * 헤더 표면(sticky 바) 클래스 override (JCG-2898). 미지정 시 기본 `bg-background/85` 유지 —
   * 다른 셸/라우트는 전부 undefined 라 기존과 완전히 동일. 파이널 모의고사(/final-mock-exam)만
   * 은은한 앰버 틴트를 주입해 본문·사이드바 톤과 상단까지 이어지게 한다. cn(tailwind-merge)이
   * base bg 를 last-wins 로 치환하되 backdrop-blur/translucency(frost)는 보존된다.
   */
  surfaceClassName?: string;
}

export default function ContentHeader({
  leftCollapsed = false,
  onToggleSidebar,
  rightCollapsed = false,
  onToggleRightSidebar,
  rightSidebarLabel = "도구",
  categoryLabel,
  scopeLabel,
  headerSlotRef,
  headerActionSlotRef,
  staticTitle,
  showSidebarToggle = true,
  surfaceClassName,
}: ContentHeaderProps) {
  const pathname = usePathname();
  const [pinned, setPinned] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const actionSlotRef = React.useRef<HTMLElement | null>(null);
  const [hasHeaderAction, setHasHeaderAction] = React.useState(false);
  const rightSidebarAction = `${rightSidebarLabel} ${rightCollapsed ? "펼치기" : "접기"}`;

  const setActionSlot = React.useCallback(
    (el: HTMLElement | null) => {
      actionSlotRef.current = el;
      headerActionSlotRef?.(el);
      setHasHeaderAction(Boolean(el?.childNodes.length));
    },
    [headerActionSlotRef],
  );

  React.useEffect(() => {
    if (!headerActionSlotRef) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasHeaderAction(false);
      return;
    }

    const el = actionSlotRef.current;
    if (!el) return;

    const syncActionState = () => {
      setHasHeaderAction(el.childNodes.length > 0);
    };

    syncActionState();
    const observer = new MutationObserver(syncActionState);
    observer.observe(el, { childList: true });
    return () => observer.disconnect();
  }, [headerActionSlotRef]);

  // ArticleShell(=ContentHeader)은 셸 페이지 간 소프트 내비게이션에서 언마운트되지 않으므로,
  // 경로가 바뀌면 새 페이지의 #article-h1 텍스트를 다시 읽어야 한다. pathname 을 deps 에 넣지
  // 않으면 첫 페이지에서 읽은 제목이 그대로 남아(stale) 다른 페이지에서 잘못된 핀 제목이 노출된다.
  React.useEffect(() => {
    // 인덱스 셸(staticTitle)은 핀 대상 h1 이 없고 title/pinned 를 렌더하지 않으므로
    // scroll-spy 리스너를 등록하지 않는다(매 스크롤 setPinned 재렌더 낭비 방지).
    if (staticTitle) return;

    const h1 = document.getElementById("article-h1");
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (h1) setTitle(h1.textContent?.trim() ?? "");

    const onScroll = () => {
      const el = document.getElementById("article-h1");
      if (!el) {
        setPinned(false);
        return;
      }
      // 헤더(약 52px) 아래로 h1 하단이 지나가면 핀
      setPinned(el.getBoundingClientRect().bottom < 52);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname, staticTitle]);

  return (
    <div
      className={cn(
        "sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-background/85 px-4 py-2.5 backdrop-blur-sm sm:px-6",
        surfaceClassName,
      )}
    >
      {showSidebarToggle && (
        <button
          type="button"
          onClick={onToggleSidebar}
          title={leftCollapsed ? "사이드바 펼치기" : "사이드바 접기"}
          aria-label={leftCollapsed ? "사이드바 펼치기" : "사이드바 접기"}
          aria-expanded={!leftCollapsed}
          className={cn(
            "inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-border px-2.5 text-[13px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
            leftCollapsed
              ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
              : "bg-card text-muted-foreground hover:text-foreground",
          )}
        >
          <PanelLeft className="h-[17px] w-[17px]" aria-hidden="true" />
          {leftCollapsed && <span>메뉴</span>}
        </button>
      )}

      {staticTitle ? (
        // 인덱스 셸(JCG-2104): scroll-spy 핀 없이 페이지 제목을 항상 표시.
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="truncate text-[15px] font-extrabold text-foreground">
            {staticTitle}
          </span>
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {/* 페이지 주입 슬롯(복원 회차 셀렉터 등, JCG-2102). 채워지면 중앙을 차지, 비면 사라짐. */}
          <div
            ref={headerSlotRef}
            className="flex min-w-0 flex-1 items-center overflow-hidden empty:hidden"
          />
          {/* 스크롤 핀 breadcrumb(이론/코딩 글) — 핀이 아닐 땐 폭 0 으로 접어 슬롯이 중앙을 차지. */}
          <div
            className={cn(
              "flex items-center gap-1.5 transition-all duration-200",
              pinned
                ? "min-w-0 flex-1 translate-y-0 opacity-100"
                : "pointer-events-none w-0 flex-none translate-y-1 overflow-hidden opacity-0",
            )}
          >
            {categoryLabel && (
              <>
                <span className="whitespace-nowrap text-[12.5px] font-semibold text-muted-foreground">
                  {categoryLabel}
                </span>
                <ChevronRight aria-hidden="true" className="h-3 w-3 shrink-0 text-muted-foreground" />
              </>
            )}
            {scopeLabel && (
              <>
                {/* 스코프 라벨: 카테고리와 동일 톤. 길면 제목 공간을 침범하지 않도록
                    max-width + truncate 로 자체 말줄임(제목 우선 노출). */}
                <span className="max-w-[40%] shrink truncate whitespace-nowrap text-[12.5px] font-semibold text-muted-foreground">
                  {scopeLabel}
                </span>
                <ChevronRight aria-hidden="true" className="h-3 w-3 shrink-0 text-muted-foreground" />
              </>
            )}
            <span className="truncate text-[15px] font-extrabold text-foreground">{title}</span>
          </div>
        </div>
      )}

      <div className="ml-auto flex min-w-0 shrink-0 items-center gap-2">
        {headerActionSlotRef && (
          <div
            ref={setActionSlot}
            className="flex min-w-0 shrink items-center gap-1.5 overflow-visible empty:hidden"
          />
        )}
        {hasHeaderAction || onToggleRightSidebar ? (
          <>
            <div className={hasHeaderAction ? "hidden 2xl:block" : "hidden xl:block"}>
              <Search />
            </div>
            <div className={hasHeaderAction ? "2xl:hidden" : "xl:hidden"}>
              <Search variant="icon" />
            </div>
          </>
        ) : (
          <Search />
        )}
        {onToggleRightSidebar && (
          <button
            type="button"
            onClick={onToggleRightSidebar}
            title={rightSidebarAction}
            aria-label={rightSidebarAction}
            aria-expanded={!rightCollapsed}
            className={cn(
              "inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border border-border px-2 text-[12px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
              rightCollapsed
                ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                : "bg-card text-muted-foreground hover:text-foreground",
            )}
          >
            <PanelRight className="h-[17px] w-[17px]" aria-hidden="true" />
            <span>{rightSidebarLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
}
